import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLoader } from '@/context/LoaderContext'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { useDisableBalanceMutation, useEnableBalancesMutation, useGetBalancesQuery, useGetCurrenciesQuery, useSetDefaultBalanceMutation } from '@/store/api/paymentApi'
import {
	clearGridSelectedCurrencyCodes,
	selectBalances,
	selectGridSelectedCurrencyCodes,
	setGridCurrencyChecked as setGridCurrencyCheckedAction,
} from '@/store/slices/paymentSlice'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import { useBalanceUpdateSocket } from '@/store/socket/balance-socket'

export function useProfileWallet() {
	const dispatch = useAppDispatch()
	const { showLoader, hideLoader } = useLoader()
	const isAuthenticated = useAppSelector(selectIsAuthenticated)
	const gridSelectedCurrencyCodes = useAppSelector(selectGridSelectedCurrencyCodes)
	const storeBalances = useAppSelector(selectBalances)
	const [disablingCurrencyCode, setDisablingCurrencyCode] = useState<string | null>(null)
	const [settingDefaultCurrencyCode, setSettingDefaultCurrencyCode] = useState<string | null>(null)

	const { data: currenciesResponse, isLoading: isCurrenciesLoading, isError: isCurrenciesError } = useGetCurrenciesQuery()
	const { isLoading: isBalancesLoading, isError: isBalancesError } = useGetBalancesQuery(undefined, { skip: !isAuthenticated })
	const [enableBalances, { isLoading: isEnablingBalances }] = useEnableBalancesMutation()
	const [disableBalance] = useDisableBalanceMutation()
	const [setDefaultBalance] = useSetDefaultBalanceMutation()

	useBalanceUpdateSocket()

	const isMutatingBalances = isEnablingBalances || Boolean(disablingCurrencyCode) || Boolean(settingDefaultCurrencyCode)

	useEffect(() => {
		if (!isMutatingBalances) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [isMutatingBalances, showLoader, hideLoader])

	const currencies = currenciesResponse?.data ?? []
	const balances = useMemo(() => {
		return [...storeBalances].sort((a, b) => {
			if (a.is_default === b.is_default) {
				return 0
			}

			return a.is_default ? -1 : 1
		})
	}, [storeBalances])

	const balanceCodes = useMemo(() => new Set(balances.map((balance) => balance.currency_code.toUpperCase())), [balances])

	const availableCurrencies = useMemo(() => currencies.filter((currency) => !balanceCodes.has(currency.currency_code.toUpperCase())), [currencies, balanceCodes])

	const handleGridCurrencyChecked = useCallback(
		(currencyCode: string, checked: boolean) => {
			dispatch(setGridCurrencyCheckedAction({ code: currencyCode, checked }))
		},
		[dispatch],
	)

	const handleApplySelectedCurrencies = useCallback(() => {
		if (!gridSelectedCurrencyCodes.length || isEnablingBalances) {
			return
		}

		void enableBalances({ currency_codes: gridSelectedCurrencyCodes })
			.unwrap()
			.then(() => {
				dispatch(clearGridSelectedCurrencyCodes())
			})
			.catch(() => {
				// Errors are handled by the global API interceptor.
			})
	}, [dispatch, enableBalances, gridSelectedCurrencyCodes, isEnablingBalances])

	const handleDisableBalance = useCallback(
		(currencyCode: string) => {
			if (disablingCurrencyCode) {
				return
			}

			setDisablingCurrencyCode(currencyCode)

			void disableBalance({ currency_code: currencyCode })
				.unwrap()
				.catch(() => {
					// Errors are handled by the global API interceptor.
				})
				.finally(() => {
					setDisablingCurrencyCode(null)
				})
		},
		[disableBalance, disablingCurrencyCode],
	)

	const handleSetDefaultBalance = useCallback(
		(currencyCode: string) => {
			if (settingDefaultCurrencyCode) {
				return
			}

			setSettingDefaultCurrencyCode(currencyCode)

			void setDefaultBalance({ currency_code: currencyCode })
				.unwrap()
				.catch(() => {
					// Errors are handled by the global API interceptor.
				})
				.finally(() => {
					setSettingDefaultCurrencyCode(null)
				})
		},
		[setDefaultBalance, settingDefaultCurrencyCode],
	)

	const canApplySelectedCurrencies = gridSelectedCurrencyCodes.length > 0 && !isEnablingBalances

	return {
		balances,
		availableCurrencies,
		gridSelectedCurrencyCodes,
		isCurrenciesLoading,
		isBalancesLoading,
		isEnablingBalances,
		disablingCurrencyCode,
		settingDefaultCurrencyCode,
		isCurrenciesError,
		isBalancesError,
		handleGridCurrencyChecked,
		handleApplySelectedCurrencies,
		handleDisableBalance,
		handleSetDefaultBalance,
		canApplySelectedCurrencies,
	}
}
